import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient, httpResource } from '@angular/common/http';
import { Role, UserInfo } from '../../utils/models/tool.models';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';

/** Standard information returned after a login request. */
export interface Jwt {
  token: string;
  success: boolean;
  msg: string;
  tos: boolean;
  needPwdChange: boolean;
}

/** The key for the token in the session storage. */
const tokenStorageKey = 'token';

/**
 *  Service to provide calls to backend considering user management
 *  e.g. creating, updating users information
 */
@Injectable({
  providedIn: 'root',
})
export class UserService {
  /** Inject services. */
  private readonly httpService = inject(HttpClient);
  private readonly router = inject(Router, { optional: true });
  private readonly jwtHelper = new JwtHelperService();

  /** The internal user state. */
  private readonly internalUser = signal<UserInfo | null>(null);
  private readonly internalAuthToken = signal<string | null>(
    sessionStorage.getItem(tokenStorageKey)
  );

  /** The currently authenticated user, or null when no user is authenticated. */
  readonly user = this.internalUser.asReadonly();

  /** The authentication token for the current browser session. */
  readonly authToken = this.internalAuthToken.asReadonly();

  /** Whether an authenticated user is available. */
  readonly isUserLoggedIn = computed(() => this.user() !== null);

  /** Whether the stored token is valid. */
  readonly isAuthenticated = computed(() => !this.jwtHelper.isTokenExpired(this.authToken()));

  /** Fetches and stores the current user's information. */
  private async loadCurrentUser(): Promise<UserInfo> {
    const user = await firstValueFrom(this.httpService.get<UserInfo>('external/users/ego'));
    this.internalUser.set(user);
    return user;
  }

  /** Stores the authentication token for the current browser session. */
  private setAuthToken(token: string): void {
    sessionStorage.setItem(tokenStorageKey, token);
    this.internalAuthToken.set(token);
  }

  /** Clears the persisted authentication token and the current user state. */
  clearSession(): void {
    sessionStorage.removeItem(tokenStorageKey);
    this.internalUser.set(null);
    this.internalAuthToken.set(null);
  }

  /** Restores the current user for a protected route. */
  async canActivate(): Promise<boolean> {
    if (!this.isAuthenticated()) {
      this.clearSession();
      void this.router?.navigate(['login']);
      return false;
    }

    try {
      await this.loadCurrentUser();
      return true;
    } catch {
      this.clearSession();
      void this.router?.navigate(['login']);
      return false;
    }
  }

  /** Logs in and persists the returned token on success. */
  async login(usr: string, pwd: string): Promise<Jwt> {
    const result = await firstValueFrom(
      this.httpService.post<Jwt>('external/sessions', { usr, pwd })
    );
    if (result.success) {
      this.setAuthToken(result.token);
    }
    return result;
  }

  /** Logs out and always clears the local session. */
  async logout(): Promise<string> {
    try {
      return await firstValueFrom(this.httpService.get<string>('external/sessions/logout'));
    } finally {
      this.clearSession();
    }
  }

  /**
   * Get all registered users
   */
  getAllUsers() {
    return httpResource<UserInfo[]>(() => 'internal/users');
  }

  /**
   * Update specific user by ID
   * Change UserName
   *
   * @param user UserInfo containing the changes
   * @returns HttpResponse with "success" or "error"
   */
  async updateUserName(user: UserInfo) {
    const payload = { userName: user.userName };
    const res = this.httpService.post<{ status: string; msg: string }>(
      `external/users/${user.userId}/name`,
      payload
    );
    const response = await firstValueFrom(res);
    if (response.status === 'success' && this.user()?.userId === user.userId) {
      this.internalUser.set(user);
    }
    return response;
  }

  /**
   * Update specific user by ID
   * Change UserName, Role, or chat access
   *
   * @param user UserInfo containing the changes
   * @returns HttpResponse with "success" or "error"
   */
  updateUserInternal(user: UserInfo) {
    const payload: Omit<UserInfo, 'userId'> = {
      userName: user.userName,
      role: user.role,
      canAccessChat: user.canAccessChat,
    };
    const res = this.httpService.post<{ status: string; msg: string }>(
      `internal/users/${user.userId}`,
      payload
    );
    return firstValueFrom(res);
  }

  /**
   * Update password of specific user
   *
   * @param userId ID of the user to change the password
   * @param passwordOld old password
   * @param passwordNew new password
   * @returns HttpResponse with "success" or "error"
   */
  updateUserPassword(userId: number, passwordOld: string, passwordNew: string) {
    const payload = { pwdOld: passwordOld, pwdNew: passwordNew };
    const res = this.httpService.post<{ success: boolean; msg: string }>(
      `external/users/${userId}/pwd`,
      payload
    );
    return firstValueFrom(res);
  }

  /**
   * Reset current user password and generate new one
   *
   * @param userId ID of the user to change the password
   * @returns HttpResponse with "success" or "error"
   */
  resetUserPassword(userId: number) {
    const res = this.httpService.get<{ success: boolean; msg: string }>(
      `internal/users/${userId}/pwd`
    );
    return firstValueFrom(res);
  }

  /**
   * Create new user
   *
   * @param userName username of the new user
   * @param role role of the user
   * @param canAccessChat whether the user can access the chat
   * @returns HttpResponse with "success" or "error"
   */
  createUser(userName: string, role: Role, canAccessChat: boolean) {
    const payload: Omit<UserInfo, 'userId'> = { userName, role, canAccessChat };
    const res = this.httpService.post<{ userId: number; pwd: string }>('internal/users', payload);
    return firstValueFrom(res);
  }
}
